import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Ear Training Suite v1 (Phase 2 Tier 2) lexicon entries — 12 interval entries +
 * 7 chord-quality entries + 1 welcome-intro entry = 20 entries. Authored by
 * Bowie in the Ear Training Suite scope memo; locked verbatim — do not edit
 * without Bowie review.
 *
 * The 4 outbound cross-link entries (crosslink-ear-to-*) live in
 * `lexicon-crosslink-entries.ts` to keep the cross-link semantics in one place.
 *
 * Spec: docs/superpowers/specs/2026-05-20-ear-training-suite-design.md §11
 * Bowie scope memo: .claude/agent-memory/bowie/project_ear_training_suite_v1_scope.md
 */
export const EAR_TRAINING_LEXICON_ENTRIES: LexiconEntry[] = [
  // ───────── 12 interval entries (category: 'interval') ─────────
  {
    id: 'interval-perfect-unison',
    category: 'interval',
    displayName: 'Perfect unison',
    displayNameShort: 'P1',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Same note.',
      intermediate: 'Perfect unison — 0 semitones.',
      advanced: 'P1 — identical pitch.',
    },
    tagLabel: { beginner: 'same', intermediate: 'unison', advanced: 'P1' },
  },
  {
    id: 'interval-minor-2nd',
    category: 'interval',
    displayName: 'Minor 2nd',
    displayNameShort: 'm2',
    pedagogicalLevel: 1,
    copy: {
      beginner: "Tense, half-step — 'Jaws' theme.",
      intermediate: 'Minor 2nd — 1 semitone; tense, unstable.',
      advanced: 'm2 — 1 semitone; the smallest diatonic interval.',
    },
    tagLabel: { beginner: 'half-step', intermediate: 'minor 2nd', advanced: 'm2' },
  },
  {
    id: 'interval-major-2nd',
    category: 'interval',
    displayName: 'Major 2nd',
    displayNameShort: 'M2',
    pedagogicalLevel: 1,
    copy: {
      beginner: "Whole step — 'Happy Birthday' opening.",
      intermediate: 'Major 2nd — 2 semitones; whole step.',
      advanced: 'M2 — 2 semitones; the diatonic whole tone.',
    },
    tagLabel: { beginner: 'whole step', intermediate: 'major 2nd', advanced: 'M2' },
  },
  {
    id: 'interval-minor-3rd',
    category: 'interval',
    displayName: 'Minor 3rd',
    displayNameShort: 'm3',
    pedagogicalLevel: 1,
    copy: {
      beginner: "The minor / sad sound — 'Greensleeves'.",
      intermediate: 'Minor 3rd — 3 semitones; defines the minor chord.',
      advanced: 'm3 — 3 semitones; 6:5 in just intonation.',
    },
    tagLabel: { beginner: 'sad 3rd', intermediate: 'minor 3rd', advanced: 'm3' },
  },
  {
    id: 'interval-major-3rd',
    category: 'interval',
    displayName: 'Major 3rd',
    displayNameShort: 'M3',
    pedagogicalLevel: 1,
    copy: {
      beginner: "The happy / major sound — 'Oh when the saints'.",
      intermediate: 'Major 3rd — 4 semitones; defines the major chord.',
      advanced: 'M3 — 4 semitones; 5:4 in just intonation.',
    },
    tagLabel: { beginner: 'bright 3rd', intermediate: 'major 3rd', advanced: 'M3' },
  },
  {
    id: 'interval-perfect-4th',
    category: 'interval',
    displayName: 'Perfect 4th',
    displayNameShort: 'P4',
    pedagogicalLevel: 1,
    copy: {
      beginner: "Strong, anchor sound — 'Here Comes the Bride'.",
      intermediate: 'Perfect 4th — 5 semitones; consonant, stable.',
      advanced: 'P4 — 5 semitones; 4:3 in just intonation.',
    },
    tagLabel: { beginner: 'anchor 4th', intermediate: 'perfect 4th', advanced: 'P4' },
  },
  {
    id: 'interval-tritone',
    category: 'interval',
    displayName: 'Tritone',
    displayNameShort: 'TT',
    pedagogicalLevel: 2,
    copy: {
      beginner: "The unsettling one — 'The Simpsons' theme.",
      intermediate: "Tritone — 6 semitones; the devil's interval.",
      advanced: 'TT — 6 semitones; aug 4th / dim 5th; unstable.',
    },
    tagLabel: { beginner: 'unsettling', intermediate: 'tritone', advanced: 'TT' },
  },
  {
    id: 'interval-perfect-5th',
    category: 'interval',
    displayName: 'Perfect 5th',
    displayNameShort: 'P5',
    pedagogicalLevel: 1,
    copy: {
      beginner: "Open, hollow sound — 'Star Wars' theme.",
      intermediate: 'Perfect 5th — 7 semitones; very consonant.',
      advanced: 'P5 — 7 semitones; 3:2 in just intonation.',
    },
    tagLabel: { beginner: 'open 5th', intermediate: 'perfect 5th', advanced: 'P5' },
  },
  {
    id: 'interval-minor-6th',
    category: 'interval',
    displayName: 'Minor 6th',
    displayNameShort: 'm6',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Bittersweet — 'The Entertainer' phrase.",
      intermediate: 'Minor 6th — 8 semitones; minor-tonality 3rd inverted.',
      advanced: 'm6 — 8 semitones; inversion of M3.',
    },
    tagLabel: { beginner: 'bittersweet', intermediate: 'minor 6th', advanced: 'm6' },
  },
  {
    id: 'interval-major-6th',
    category: 'interval',
    displayName: 'Major 6th',
    displayNameShort: 'M6',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Wistful, longing — 'My Bonnie'.",
      intermediate: 'Major 6th — 9 semitones; common in folk melody.',
      advanced: 'M6 — 9 semitones; inversion of m3.',
    },
    tagLabel: { beginner: 'longing', intermediate: 'major 6th', advanced: 'M6' },
  },
  {
    id: 'interval-minor-7th',
    category: 'interval',
    displayName: 'Minor 7th',
    displayNameShort: 'm7',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The dominant-7 leading sound.',
      intermediate: 'Minor 7th — 10 semitones; characteristic of dom7 chords.',
      advanced: 'm7 — 10 semitones; inversion of M2.',
    },
    tagLabel: { beginner: 'bluesy 7th', intermediate: 'minor 7th', advanced: 'm7' },
  },
  {
    id: 'interval-major-7th',
    category: 'interval',
    displayName: 'Major 7th',
    displayNameShort: 'M7',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Reaching, near-octave — 'Take On Me'.",
      intermediate: 'Major 7th — 11 semitones; sharp tension, near-octave.',
      advanced: 'M7 — 11 semitones; inversion of m2.',
    },
    tagLabel: { beginner: 'reaching', intermediate: 'major 7th', advanced: 'M7' },
  },

  // ───────── 7 chord-quality entries (category: 'quality') ─────────
  {
    id: 'quality-major',
    category: 'quality',
    displayName: 'Major',
    displayNameShort: 'maj',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The bright, happy chord.',
      intermediate: 'Major triad — root + major 3rd + perfect 5th.',
      advanced: 'Major triad — 4-3 stack (M3 + m3) on root.',
    },
    tagLabel: { beginner: 'bright', intermediate: 'major', advanced: 'maj' },
  },
  {
    id: 'quality-minor',
    category: 'quality',
    displayName: 'Minor',
    displayNameShort: 'min',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The sad / melancholic chord.',
      intermediate: 'Minor triad — root + minor 3rd + perfect 5th.',
      advanced: 'Minor triad — 3-4 stack (m3 + M3) on root.',
    },
    tagLabel: { beginner: 'sad', intermediate: 'minor', advanced: 'min' },
  },
  {
    id: 'quality-diminished',
    category: 'quality',
    displayName: 'Diminished',
    displayNameShort: 'dim',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Tense, unresolved — wants to move.',
      intermediate: 'Diminished triad — minor 3rd + diminished 5th.',
      advanced: 'dim triad — 3-3 stack (m3 + m3); unstable.',
    },
    tagLabel: { beginner: 'tense', intermediate: 'diminished', advanced: 'dim' },
  },
  {
    id: 'quality-augmented',
    category: 'quality',
    displayName: 'Augmented',
    displayNameShort: 'aug',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Mysterious, ambiguous — no clear resolution.',
      intermediate: 'Augmented triad — major 3rd + augmented 5th.',
      advanced: 'aug triad — 4-4 stack (M3 + M3); symmetric.',
    },
    tagLabel: { beginner: 'mysterious', intermediate: 'augmented', advanced: 'aug' },
  },
  {
    id: 'quality-dominant-7',
    category: 'quality',
    displayName: 'Dominant 7',
    displayNameShort: 'dom7',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Bluesy, leading — pulls home.',
      intermediate: 'Dominant 7 — major triad + minor 7th.',
      advanced: 'Dom7 — V7 quality; tritone in 3rd–7th.',
    },
    tagLabel: { beginner: 'bluesy', intermediate: 'dominant 7', advanced: 'dom7' },
  },
  {
    id: 'quality-major-7',
    category: 'quality',
    displayName: 'Major 7',
    displayNameShort: 'maj7',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Jazzy, dreamy — soft tension.',
      intermediate: 'Major 7 — major triad + major 7th.',
      advanced: 'Maj7 — Imaj7 / IVmaj7 quality; M3 above 5th.',
    },
    tagLabel: { beginner: 'jazzy', intermediate: 'major 7', advanced: 'maj7' },
  },
  {
    id: 'quality-minor-7',
    category: 'quality',
    displayName: 'Minor 7',
    displayNameShort: 'min7',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Smooth, jazz — relaxed.',
      intermediate: 'Minor 7 — minor triad + minor 7th.',
      advanced: 'm7 — iim7 / vim7 quality; m3 above 5th.',
    },
    tagLabel: { beginner: 'smooth', intermediate: 'minor 7', advanced: 'min7' },
  },

  // ───────── 1 session-wrapper entry (category: 'ear-training') ─────────
  {
    id: 'ear-training-welcome-intro',
    category: 'ear-training',
    displayName: 'Ear training intro',
    displayNameShort: 'Intro',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Listen and identify what you hear.',
      intermediate: 'Hear a sound — pick what it is from the answer buttons.',
      advanced: 'Identify the structural property by ear, regardless of pitch.',
    },
    tagLabel: { beginner: 'how it works', intermediate: 'how to play', advanced: 'methodology' },
  },

  // ───────── v1.5 sub-mode + direction-picker entries (category: 'ear-training') ─────────
  {
    id: 'ear-training-direction-picker',
    category: 'ear-training',
    displayName: 'Interval direction',
    displayNameShort: 'Direction',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Pick how the notes are played — one after another or together.',
      intermediate: 'Choose ascending, descending, harmonic, or mixed playback.',
      advanced: 'Direction shapes interval perception — ascending vs descending are distinct skills.',
    },
    tagLabel: { beginner: 'direction', intermediate: 'direction', advanced: 'playback mode' },
  },
  {
    id: 'ear-training-aug-dim-warmup',
    category: 'ear-training',
    displayName: 'Aug/Dim Warm-up',
    displayNameShort: 'Aug/Dim',
    pedagogicalLevel: 2,
    copy: {
      beginner:
        'Sharpen your ear on the unusual chords — augmented and diminished — by hearing them next to major / minor.',
      intermediate: 'Compare augmented and diminished triads against their major / minor references.',
      advanced:
        'Warm up on aug+dim symmetric triads via 2-stimulus comparison against the parallel major / minor reference.',
    },
    tagLabel: { beginner: 'unusual chords', intermediate: 'aug+dim', advanced: 'symmetric triads' },
  },
  {
    id: 'ear-training-warmup-feedback',
    category: 'ear-training',
    displayName: 'Aug/Dim warm-up feedback',
    displayNameShort: 'Feedback',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Augmented chords have a stretched-out top; diminished chords sound squeezed.',
      intermediate:
        'Augmented = ♯5 (stretched fifth); diminished = ♭5 (squeezed fifth). Both lack a stable tonal center.',
      advanced:
        'Symmetric triads — aug (M3+M3) divides the octave into 3 equal parts; dim (m3+m3) into 4. Both lack a perfect 5th, so neither has a stable tonal center.',
    },
    tagLabel: { beginner: 'symmetric', intermediate: '♯5 / ♭5', advanced: 'symmetric triads' },
  },
]
